import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { api } from "@/lib/handball";

/**
 * Renders a line chart of save % per match session for a given goalkeeper.
 * ONLY includes matches (session_type == 'match') with total shots >= 10.
 */
export default function TrendChart({ goalkeeperId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!goalkeeperId) return;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/trend", {
          params: { goalkeeper_id: goalkeeperId, min_shots: 10 },
        });
        setData(res.data);
      } catch {
        setData([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [goalkeeperId]);

  if (loading) {
    return (
      <div className="card-tactical p-6" data-testid="trend-loading">
        <div className="section-hdr mb-4">
          <span className="marker" />
          Trend · Räddningsprocent per match
        </div>
        <div className="text-zinc-500 text-sm">Laddar…</div>
      </div>
    );
  }

  const chartData = (data || []).map((s) => ({
    ...s,
    dateShort: format(new Date(s.date), "d MMM", { locale: sv }),
    dateLong: format(new Date(s.date), "d MMM yyyy", { locale: sv }),
    pctRounded: Math.round(s.save_pct * 10) / 10,
  }));

  const avg =
    chartData.length > 0
      ? chartData.reduce((a, c) => a + c.save_pct, 0) / chartData.length
      : 0;

  // Trend indicator: compare average of first half vs second half
  let trendDirection = "flat";
  if (chartData.length >= 4) {
    const half = Math.floor(chartData.length / 2);
    const firstAvg =
      chartData.slice(0, half).reduce((a, c) => a + c.save_pct, 0) / half;
    const secondAvg =
      chartData.slice(-half).reduce((a, c) => a + c.save_pct, 0) / half;
    if (secondAvg - firstAvg > 3) trendDirection = "up";
    else if (firstAvg - secondAvg > 3) trendDirection = "down";
  }

  return (
    <div className="card-tactical p-6" data-testid="trend-chart">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="section-hdr">
          <span className="marker" />
          Trend · Räddningsprocent per match
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display">
          Endast matcher · Min. 10 skott
        </div>
      </div>

      {chartData.length === 0 ? (
        <div
          className="text-zinc-500 text-sm py-8 text-center"
          data-testid="trend-empty"
        >
          Inga matcher med minst 10 registrerade skott ännu.
          <br />
          När du spelat och registrerat 10+ skott per match visas kurvan här.
        </div>
      ) : (
        <>
          <div className="flex items-center gap-6 mb-4 flex-wrap">
            <TrendStat
              label="Matcher i trend"
              value={chartData.length}
              testid="trend-count"
            />
            <TrendStat
              label="Snitt räddning"
              value={`${avg.toFixed(1)}%`}
              testid="trend-avg"
              accent
            />
            <TrendStat
              label="Riktning"
              value={
                trendDirection === "up" ? (
                  <span className="inline-flex items-center gap-1 text-[#34C759]">
                    <TrendingUp size={18} /> Uppåt
                  </span>
                ) : trendDirection === "down" ? (
                  <span className="inline-flex items-center gap-1 text-[#FF3B30]">
                    <TrendingDown size={18} /> Nedåt
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-zinc-400">
                    <Minus size={18} /> Stabil
                  </span>
                )
              }
              testid="trend-direction"
            />
          </div>

          <div style={{ width: "100%", height: 260 }} data-testid="trend-svg">
            <ResponsiveContainer>
              <LineChart
                data={chartData}
                margin={{ top: 10, right: 16, left: 0, bottom: 8 }}
              >
                <CartesianGrid stroke="#27272A" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="dateShort"
                  stroke="#71717A"
                  tick={{ fill: "#A1A1AA", fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: "#27272A" }}
                />
                <YAxis
                  domain={[0, 100]}
                  ticks={[0, 25, 50, 75, 100]}
                  stroke="#71717A"
                  tick={{ fill: "#A1A1AA", fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: "#27272A" }}
                  unit="%"
                />
                <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#34C759", strokeWidth: 1, strokeDasharray: "3 3" }} />
                <ReferenceLine
                  y={avg}
                  stroke="#FFD60A"
                  strokeDasharray="4 4"
                  strokeOpacity={0.6}
                  label={{
                    value: `Snitt ${avg.toFixed(0)}%`,
                    fill: "#FFD60A",
                    fontSize: 10,
                    position: "insideTopRight",
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="pctRounded"
                  stroke="#34C759"
                  strokeWidth={2.5}
                  dot={{ fill: "#34C759", stroke: "#0A0A0A", strokeWidth: 2, r: 5 }}
                  activeDot={{ r: 7, fill: "#34C759", stroke: "#ffffff", strokeWidth: 2 }}
                  isAnimationActive
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}

function TrendStat({ label, value, accent, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display">
        {label}
      </div>
      <div
        className="font-display font-black text-xl mt-1"
        style={{ color: accent ? "#34C759" : "#ffffff" }}
      >
        {value}
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-[#0A0A0A] border border-[#27272A] p-3 text-xs">
      <div className="font-display font-bold uppercase tracking-widest text-white mb-1">
        {p.name}
      </div>
      <div className="text-zinc-500 mb-2">
        {p.dateLong}
        {p.opponent ? ` · vs ${p.opponent}` : ""}
      </div>
      <div className="flex items-center gap-3">
        <span className="text-[#34C759] font-mono-num font-bold text-lg">
          {p.pctRounded}%
        </span>
        <span className="text-zinc-400 font-mono-num">
          {p.saves}/{p.total} räddningar
        </span>
      </div>
    </div>
  );
}
